import type { ParallaxSvgSpec } from '../../../../config/atmosphere/effectLayer.types';
import { placeWorldShape, type ShapePlacement } from './worldShapeKit';

/** Tall far planes keep horizontal landmark sizes deliberate. The parallax
 * renderer scales both axes by height/viewBoxHeight, so each caller uses the
 * matching height below. Near banks, lights and playable terrain stay separate. */
function tallTile(height: number, shapes: readonly ShapePlacement[], ground = ''): ParallaxSvgSpec {
  return { tile: { viewBoxWidth: 2400, viewBoxHeight: height,
    path: [ground, ...shapes.map(shape => placeWorldShape(...shape))].filter(Boolean).join(' ') } };
}

export const JOURNEY_DEPTH_HEIGHTS = {
  outskirts: 1400, brackenmoor: 1040, lanternwood: 1400, lanternroot: 1740, coil: 1360,
} as const;

export const JOURNEY_DEPTH_BACKDROPS = {
  outskirts: tallTile(JOURNEY_DEPTH_HEIGHTS.outskirts, [],
    'M0 1400V820L150 800Q210 780 250 700L270 560L320 540L340 400'
    + 'L410 380L440 260Q480 235 540 240L560 180L710 170L750 210L840 210'
    + 'L860 340L910 350L930 500Q970 515 1010 520L1030 680L1100 700'
    + 'Q1160 810 1230 810L1340 780L1360 630L1430 620L1450 470L1510 460'
    + 'L1550 300L1630 280L1650 220L1800 210L1840 260L1900 260'
    + 'L1920 400L1980 410L2000 570L2070 580L2100 710'
    + 'Q2180 785 2260 810L2400 820V1400Z'),
  brackenmoor: tallTile(JOURNEY_DEPTH_HEIGHTS.brackenmoor, [
    ['obelisk', 400, 870, 60, 380],
    ['obelisk', 1400, 850, 58, 310],
  ], 'M0 1040V850L170 842L290 750L420 700L510 480L570 470L680 620'
    + 'L820 700L1060 820L1230 790L1390 650L1510 520L1610 450L1690 470'
    + 'L1830 690L2040 790L2220 840L2400 850V1040Z'),
  // No ground polygon: narrow trunks leave open air between the high boughs.
  lanternwood: tallTile(JOURNEY_DEPTH_HEIGHTS.lanternwood, [
    ['snag', 100, 1400, 260, 1250], ['snag', 650, 1400, 200, 1150],
    ['snag', 1130, 1400, 300, 1320], ['snag', 1810, 1400, 260, 1200],
  ]),
  // The old crag fill became a broad wall when enlarged. Separated roots give
  // the rim enclosure without turning the deeper routes into a solid curtain.
  lanternroot: tallTile(JOURNEY_DEPTH_HEIGHTS.lanternroot, [
    ['snag', 140, 1740, 240, 1660], ['snag', 730, 1740, 200, 1520],
    ['snag', 1260, 1740, 280, 1640], ['snag', 1940, 1740, 220, 1580],
  ]),
  coil: tallTile(JOURNEY_DEPTH_HEIGHTS.coil, [
    ['column', 150, 1360, 100, 1240], ['column', 780, 1360, 90, 1160],
    ['column', 1480, 1360, 110, 1300], ['column', 2120, 1360, 95, 1250],
    ['ribs', 920, 1360, 520, 1100],
  ]),
} satisfies Record<keyof typeof JOURNEY_DEPTH_HEIGHTS, ParallaxSvgSpec>;
